import { type ComponentPropsWithoutRef, type ReactNode, createContext, useContext } from 'react'

import { Progress, type ProgressProps } from '@ps/ds/components/primitives/Progress/Progress'
import { cn } from '@ps/ds/lib/utils'
import { CardDescription, CardTitle, CardHeader as PrimitiveCardHeader } from '@ps/ds/ui/card'

export type CardHeaderSize = 'sm' | 'md' | 'lg' | 'xl'
export type CardHeaderPadding = 'none' | 'sm' | 'md' | 'lg'
type CardHeaderSizeGroup = 'compact' | 'large'

const cardHeaderPaddingClasses = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
} as const

const titleBaseClasses = 'font-brand font-bold leading-none tracking-tight text-heading'

const titleTextSizeClasses = {
  sm: 'text-base',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl',
} as const

const sizeGroupByHeaderSize: Record<CardHeaderSize, CardHeaderSizeGroup> = {
  sm: 'compact',
  md: 'compact',
  lg: 'large',
  xl: 'large',
} as const

const descriptionSizeByGroup: Record<CardHeaderSizeGroup, string> = {
  compact: 'text-sm',
  large: 'text-base',
} as const

const descriptionIconSizeByGroup: Record<CardHeaderSizeGroup, string> = {
  compact: 'size-4',
  large: 'size-5',
} as const

const progressSizeByGroup: Record<CardHeaderSizeGroup, NonNullable<ProgressProps['size']>> = {
  compact: 'xs',
  large: 'sm',
} as const

type CardHeaderContextValue = {
  size: CardHeaderSize
  isCentered: boolean
}

const cardHeaderContext = createContext<CardHeaderContextValue>({
  size: 'md',
  isCentered: false,
})

const useCardHeaderContext = () => useContext(cardHeaderContext)

export interface CardHeaderProps
  extends Omit<ComponentPropsWithoutRef<typeof PrimitiveCardHeader>, 'title'> {
  // whether the card header text should be centered
  isCentered?: boolean
  // overall size of the card header
  size?: CardHeaderSize
  // padding for the header
  padding?: CardHeaderPadding
}

export interface CardHeaderContainerProps extends ComponentPropsWithoutRef<'div'> {}

export interface CardHeaderTitleProps extends ComponentPropsWithoutRef<typeof CardTitle> {
  grow?: boolean
}

export interface CardHeaderDescriptionProps
  extends ComponentPropsWithoutRef<typeof CardDescription> {
  icon?: ReactNode
}

export interface CardHeaderProgressProps extends ProgressProps {}

export const CardHeader = ({
  className,
  children,
  isCentered = false,
  size = 'md',
  padding = 'md',
  ...props
}: CardHeaderProps) => {
  return (
    <PrimitiveCardHeader
      className={cn(cardHeaderPaddingClasses[padding], isCentered && 'text-center', className)}
      {...props}>
      <cardHeaderContext.Provider value={{ size, isCentered }}>
        {children}
      </cardHeaderContext.Provider>
    </PrimitiveCardHeader>
  )
}

export const CardHeaderContainer = ({ className, ...props }: CardHeaderContainerProps) => {
  const { isCentered } = useCardHeaderContext()

  return (
    <div
      className={cn('flex w-full items-center gap-2', isCentered && 'justify-center', className)}
      {...props}
    />
  )
}

export const CardHeaderTitle = ({ className, grow = true, ...props }: CardHeaderTitleProps) => {
  const { size } = useCardHeaderContext()

  return (
    <CardTitle
      className={cn(titleBaseClasses, titleTextSizeClasses[size], grow && 'flex-1', className)}
      {...props}
    />
  )
}

export const CardHeaderDescription = ({
  className,
  icon,
  children,
  ...props
}: CardHeaderDescriptionProps) => {
  const { size, isCentered } = useCardHeaderContext()
  const resolvedSizeGroup = sizeGroupByHeaderSize[size]

  return (
    <CardDescription
      className={cn(
        'flex items-start gap-1.5',
        descriptionSizeByGroup[resolvedSizeGroup],
        isCentered && 'justify-center',
        className,
      )}
      {...props}>
      {icon && (
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex shrink-0 items-center justify-center [&_svg]:size-full',
            descriptionIconSizeByGroup[resolvedSizeGroup],
          )}>
          {icon}
        </span>
      )}
      <span>{children}</span>
    </CardDescription>
  )
}

export const CardHeaderProgress = ({
  className,
  size: progressSize,
  ...props
}: CardHeaderProgressProps) => {
  const { size: headerSize } = useCardHeaderContext()
  const resolvedSizeGroup = sizeGroupByHeaderSize[headerSize]

  return (
    <Progress
      {...props}
      size={progressSize ?? progressSizeByGroup[resolvedSizeGroup]}
      className={cn('w-full', className)}
    />
  )
}
